// ChatBot.jsx
import "../css/App.css";
import { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  InputGroup,
  Spinner,
  Offcanvas,
} from "react-bootstrap";
import jacaiLogo from "../images/JACAiLogo.png";
import CompactDisclaimer from "../components/CompactDisclaimer.jsx";
import ReactMarkdown from "react-markdown";
import { getFirebaseUserId } from "../firebase/firebase.js";
import "../css/Chat.css";
import ThreadsSidebar from "../components/ThreadsSidebar.jsx";
import SidebarContent from "../components/SidebarContent.jsx";
import { useThreads } from "../hooks/useThreads.js";
import TextareaAutosize from "react-textarea-autosize";
import StrangerThingsOverlay from '../components/StrangerThingsPortalOverlay.jsx';
import { getMatchedKeyword } from '../helpers/SpecialWords.js';
import MessageActions from '../components/MessageActions.jsx';
import PdfUploadModal from "../components/PdfUploadModal.jsx";
import remarkGfm from "remark-gfm";
import Seo from "../components/Seo.jsx";

const educationalChatStructuredData = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Educational Chat JACAi",
  alternateName: "JACAi Educational Chat",
  url: "https://chatjacai.com/educational-chat",
  applicationCategory: "EducationalApplication",
  operatingSystem: "Web",
  isAccessibleForFree: true,
  description: "Asistent AI educațional pentru explicații clare, învățare și informare.",
  publisher: { "@type": "Organization", name: "JACAi", url: "https://chatjacai.com/" },
};

const ChatBot = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  const year = new Date().getFullYear();
  const messagesEndRef = useRef(null);

  const [showUpload, setShowUpload] = useState(false);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [userId, setUserId] = useState(null);
  const [overlayStrangerThings, setOverlayStrangerThings] = useState(false);
  const [isGeneratingCv] = useState(false);
  const markdownPlugins = [remarkGfm];

  useEffect(() => {
    document.body.classList.add("body-no-scroll");

    return () => {
      document.body.classList.remove("body-no-scroll");
    };
  }, []);

  useEffect(() => {
    getFirebaseUserId()
      .then((uid) => setUserId(uid))
      .catch((err) => console.error("Failed to get Firebase user", err));
  }, []);

  const handleOpenMenu = () => setShowMenu(true);
  const handleCloseMenu = () => setShowMenu(false);

  const {
    threads,
    activeThreadId,
    currentMessages,
    addMessageToActiveThread,
    newThread,
    selectThread,
    updateMessageInActiveThread,
    renameActiveThreadIfDefault,
    loadMoreThreads,
    hasMore
  } = useThreads({ userId });

  useEffect(() => {
    if (isSending) return;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [currentMessages, isSending]);

  const handleSfOverlayFinished = () => {
    setOverlayStrangerThings(false);
  };

  const overlayHandlers = {
    "stranger things": () => setOverlayStrangerThings(true)
  };

  const handleCancelCv = async () => {
    setIsThinking(true);
    setIsSending(true);
    handleCloseMenu();

    try {
      const uid = userId || (await getFirebaseUserId());

      const body = {
        userId: uid,
        threadId: activeThreadId,
      };

      const res = await fetch(`${API_URL}/cv-cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP error ${res.status}`);

      const botMsgId = Date.now() + 1;
      addMessageToActiveThread({
        id: botMsgId,
        from: "assistant",
        text: "Ok, I've stopped CV.",
        ts: new Date().toISOString(),
      });
    } catch (err) {
      console.error(err);

      const botMsgId = Date.now() + 2;
      addMessageToActiveThread({
        id: botMsgId,
        from: "assistant",
        text: "Oops, I couldnt stop it, Try Again.",
        ts: new Date().toISOString(),
      });
    } finally {
      setIsSending(false);
      setIsThinking(false);
    }
  };

  const handleStartCv = async () => {
    setIsThinking(true);
    setIsSending(true);
    handleCloseMenu();

    try {
      const uid = userId || (await getFirebaseUserId());

      const body = {
        userId: uid,
        threadId: activeThreadId,
      };

      const res = await fetch(`${API_URL}/cv-start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP error ${res.status}`);

      const botMsgId = Date.now() + 1;
      addMessageToActiveThread({
        id: botMsgId,
        from: "assistant",
        text: data.reply || "Ok. Tell me your name, title and email.",
        ts: new Date().toISOString(),
      });
    } catch (err) {
      console.error(err);

      const botMsgId = Date.now() + 2;
      addMessageToActiveThread({
        id: botMsgId,
        from: "assistant",
        text: "Oops, something went wrong. Please try again.",
        ts: new Date().toISOString(),
      });
    } finally {
      setIsSending(false);
      setIsThinking(false);
    }
  };

  async function handleGenerateCvPdf() {
    const res = await fetch(`${API_URL}/cv-generate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        userId,
        threadId: activeThreadId,
        activeThreadId: activeThreadId,
        action: "cv_generate"
      })
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || "Failed to generate CV");
    }

    const blob = await res.blob();

    const url = window.URL.createObjectURL(blob);

    window.open(url, "_blank");

    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  const sendChatMessage = async (text, existingBotMsgId) => {
    setIsThinking(true);
    setIsSending(true);

    try {
      const uid = userId || (await getFirebaseUserId());

      const body = {
        userId: uid,
        text,
        threadId: activeThreadId,
      };

      const res = await fetch(`${API_URL}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error(`HTTP error ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let fullText = "";

      let botMsgId = existingBotMsgId;

      if (!botMsgId) {
        botMsgId = Date.now() + 1;
        addMessageToActiveThread({
          id: botMsgId,
          from: "assistant",
          text: "",
          ts: new Date().toISOString(),
        });
      } else {
        updateMessageInActiveThread(botMsgId, { text: "" });
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        fullText += chunk;

        updateMessageInActiveThread(botMsgId, { text: fullText });
      }
    } catch (err) {
      console.error(err);

      const errorMsgId = existingBotMsgId || Date.now() + 2;

      updateMessageInActiveThread(
        errorMsgId,
        { text: "Oops, something went wrong. Please try again." }
      );
    } finally {
      setIsSending(false);
      setIsThinking(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();

    const trimmed = input.trim();
    if (!trimmed || isSending || !activeThreadId) {
      return;
    }

    const matched = getMatchedKeyword(trimmed);
    if (matched && overlayHandlers[matched]){
      overlayHandlers[matched]();
      setInput("");
      return;
    }

    const userMsg = {
      id: Date.now(),
      from: "user",
      text: trimmed,
      ts: new Date().toISOString(),
    };
    addMessageToActiveThread(userMsg);

    renameActiveThreadIfDefault(trimmed);

    setInput("");

    await sendChatMessage(trimmed);
  };

  const handleFeedback = async (messageId, useful) => {
    try {
      const uid = userId || (await getFirebaseUserId());

      updateMessageInActiveThread(messageId, {
        feedback: useful ? "up" : "down",
      });

      await fetch(`${API_URL}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: uid,
          threadId: activeThreadId,
          messageId,
          useful,
        }),
      });
    } catch (err) {
      console.error("Failed to send feedback", err);
    }
  };

  const handleRetry = async (botMessageId) => {
    if (isSending || !activeThreadId) return;

    const lastUserMessage = [...currentMessages]
      .reverse()
      .find((m) => m.from === "user");

    if (!lastUserMessage) return;

    await sendChatMessage(lastUserMessage.text, botMessageId);
  };

  const handleCopy = (messageId) => {
    const msg = currentMessages.find((m) => m.id === messageId);
    if (!msg) return;
    navigator.clipboard.writeText(msg.text);
  };

  const handlePdfSelected = async (file) => {
    if (!file || isSending || !activeThreadId) return;

    const tempMessageId = Date.now() + 3;

    try {
      const uid = userId || (await getFirebaseUserId());

      addMessageToActiveThread({
        id: tempMessageId,
        from: "assistant",
        text: `📄 Received file: "${file.name}". Processing your CV...`,
        ts: new Date().toISOString(),
        status: "loading",
        type: "cv_upload_status"
      });

      handleCloseMenu();
      setIsSending(true);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("userId", uid);
      formData.append("threadId", activeThreadId);

      const res = await fetch(`${API_URL}/cv-upload`, {
        method: "POST",
        body: formData
      });

      if (!res.ok) {
        throw new Error(`Upload failed with status ${res.status}`);
      }

      const data = await res.json();
      
      updateMessageInActiveThread(tempMessageId, {
        text:
          data.analysis ||
          "I analyzed your CV, but the server did not return a clear result.",
        status: "done"
      });

      /*if (data.cvText) {
        setInput(data.cvText);
      }*/
    } catch (error) {
      console.error("Error uploading or processing PDF:", error);

      updateMessageInActiveThread(tempMessageId, {
        text: "❌ An error occurred while uploading or analyzing your CV.",
        status: "error"
      });
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 992) {
        setShowMenu(false);
      }
    };

    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <>
      <Seo
        title="Educational Chat JACAi - Asistent AI pentru învățare"
        description="Folosește Educational Chat JACAi pentru explicații clare, întrebări educaționale și suport AI pentru învățare."
        path="/educational-chat"
        image="https://chatjacai.com/og-image.png"
        structuredData={educationalChatStructuredData}
      />
      <h1 className="visually-hidden">
        JACAi - AI Chatbot in Romanian and English
      </h1>
      <p className="visually-hidden">
        JACAi is an AI-powered chatbot capable of answering questions in English and 
        Romanian about programming, technology, science, history, culture, and more.
      </p>
      <div className="chat-page jacai-page">
        <Offcanvas
          show={showMenu}
          onHide={handleCloseMenu}
          placement="end"
          className="d-lg-none"
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>Menu</Offcanvas.Title>
          </Offcanvas.Header>

          <Offcanvas.Body className="d-flex flex-column justify-content-between">
            <div className="jacai-meta-row jacai-meta-row-mobile mt-1">
              <span className="jacai-meta-chip">
                <span className="jacai-meta-label">ACTIVE THREAD:</span>
                <span className="jacai-meta-value">{activeThreadId || "—"}</span>
              </span>

              <span className="jacai-meta-chip">
                <span className="jacai-meta-label">USER:</span>
                <span className="jacai-meta-value">{userId || "guest"}</span>
              </span>
            </div>
            <SidebarContent
              year={year}
              threads={threads}
              activeThreadId={activeThreadId}
              handlePdfUpload={handlePdfSelected}
              onNewThread={newThread}
              onSelectThread={(id) => {
                selectThread(id);
                handleCloseMenu();
              }}
              onLoadMoreThreads={loadMoreThreads}
              hasMoreThreads={hasMore}
              isMobile={true}
              handleStartCv={handleStartCv}
              isGeneratingCv={isGeneratingCv}
              handleGenerateCvPdf={handleGenerateCvPdf}
              handleCancelCv={handleCancelCv}
            />
            <div className="text-center mt-4 pt-3 border-top">
              <small className="text-muted">&copy; {year} JACAi</small>
            </div>
          </Offcanvas.Body>
        </Offcanvas>

        <Container fluid className="flex-grow-1 d-flex flex-column">
          <Row className="flex-grow-1 d-flex">
            <Col md={3} className="d-none d-lg-flex flex-column">
              <ThreadsSidebar
                year={year}
                threads={threads}
                activeThreadId={activeThreadId}
                handlePdfUpload={handlePdfSelected}
                onNewThread={newThread}
                onSelectThread={selectThread}
                onLoadMoreThreads={loadMoreThreads}
                hasMoreThreads={hasMore}
                handleStartCv={handleStartCv}
                isGeneratingCv={isGeneratingCv}
                handleGenerateCvPdf={handleGenerateCvPdf}
                handleCancelCv={handleCancelCv}
              />
            </Col>

            <Col
              xs={12}
              lg={9}
              className="d-flex align-items-stretch justify-content-center chat-main-col"
            >
              <div
                className="w-100 h-100 d-flex justify-content-center"
                style={{ maxWidth: "992px" }}
              >
                <Card className="shadow-sm d-flex flex-column flex-grow-1 chat-card jacai-card">
                  <Card.Header className="jacai-header">
                    <div className="jacai-header-mobile d-flex d-lg-none">
                      <div className="jacai-header-mobile-main flex-grow-1">
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <div className="jacai-avatar-wrapper-mini">
                            <div className="jacai-avatar-ring-mini" />
                            <img
                              src={jacaiLogo}
                              alt="Jacai Logo"
                              className="jacai-logo-mini"
                            />
                          </div>

                          <span className="jacai-title mb-0">JACAI</span>
                        </div>
                        <small className="jacai-subtitle">
                          Just A Chill Ai · powered by Ollama
                        </small>
                        <div className="jacai-meta-row mt-1">
                          <span className="jacai-meta-chip">
                            <span className="jacai-meta-label">THREAD</span>
                            <span className="jacai-meta-value">
                              {activeThreadId || "—"}
                            </span>
                          </span>

                          <span className="jacai-meta-chip">
                            <span className="jacai-meta-label">USER</span>
                            <span className="jacai-meta-value">
                              {userId || "guest"}
                            </span>
                          </span>
                        </div>
                      </div>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="jacai-menu-btn me-2"
                        onClick={handleOpenMenu}
                      >
                        ☰
                      </Button>
                    </div>
                    <div className="jacai-header-desktop d-none d-lg-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center flex-grow-1 gap-3">

                        <div className="d-flex flex-column">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <span className="jacai-title">JACAI</span>
                          </div>

                          <small className="jacai-subtitle">
                            Just A Chill Ai · powered by Ollama
                          </small>

                          <div className="jacai-meta-row mt-1">
                            <span className="jacai-meta-chip">
                              <span className="jacai-meta-label">THREAD:</span>
                              <span className="jacai-meta-value">{activeThreadId || "—"}</span>
                            </span>

                            <span className="jacai-meta-chip">
                              <span className="jacai-meta-label">USER:</span>
                              <span className="jacai-meta-value">{userId || "guest"}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="jacai-avatar-wrapper ms-3">
                        <div className="jacai-avatar-ring" />
                        <img src={jacaiLogo} alt="Jacai Logo" className="jacai-logo" />
                      </div>
                    </div>
                  </Card.Header>
                  <Card.Body className="chat-messages d-flex flex-column flex-grow-1 overflow-auto">
                    {currentMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`d-flex mb-2 ${
                          msg.from === "user"
                            ? "justify-content-end"
                            : "justify-content-start"
                        }`}
                      >
                        <div
                          className={
                            msg.from === "user"
                              ? "jacai-bubble jacai-bubble-user"
                              : "jacai-bubble jacai-bubble-bot"
                          }
                        >
                          <ReactMarkdown remarkPlugins={markdownPlugins}>{msg.text}</ReactMarkdown>
                            {msg.from === "assistant" && (
                              <MessageActions
                                messageId={msg.id}
                                feedback={msg.feedback}
                                onCopy={handleCopy}
                                onFeedback={handleFeedback}
                                onRetry={handleRetry}
                              />
                            )}
                        </div>
                      </div>
                    ))}

                    {isThinking && (
                      <div className="jacai-thinking-glow">
                        <span className="glyphs">
                          I'm thinking
                        </span>
                      </div>
                    )}

                    <div ref={messagesEndRef} />
                  </Card.Body>

                  <Card.Footer className="chat-input-footer jacai-footer">
                    <div className="chat-input-wrapper">
                      <Form onSubmit={handleSend}>
                        <InputGroup>
                          <TextareaAutosize
                            minRows={1}
                            maxRows={10}
                            placeholder="Ask me something..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            disabled={isSending}
                            className="form-control jacai-input"
                            style={{ resize: "none" }}
                          />

                          <Button
                            type="submit"
                            disabled={!input.trim() || isSending}
                            className="jacai-send-btn"
                          >
                            {isSending ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Sending…
                              </>
                            ) : (
                              "Send"
                            )}
                          </Button>
                        </InputGroup>
                      </Form>
                    </div>
                    <CompactDisclaimer />
                    <div className="jacai-resume-button">
                      <Button
                        className="jacai-sidebar-btn btn-resume w-100 text-center"
                        onClick={() => setShowUpload(true)}
                      >
                        Analyze your resume (PDF)
                      </Button>
              
                      <PdfUploadModal
                        show={showUpload}
                        onClose={() => setShowUpload(false)}
                        onFileSelected={handlePdfSelected}
                      />
                    </div>
                  </Card.Footer>
                </Card>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      <StrangerThingsOverlay active={overlayStrangerThings} onFinish={handleSfOverlayFinished}/>
    </>
  );
};

export default ChatBot;
